// Librario Frontend - Main Entry Point

import './css/theme.css';
import { Config, CatalogConfig } from './types';
import {
  createDefaultConfig,
  encodeConfig,
  decodeConfig,
  createShareableConfig,
  mapNumericSortToString,
  mapStringSortToNumeric,
} from './config';
import { setupAuthUI, setOnAuthSuccessCallback } from './auth';
import {
  renderCatalogList,
  updateCatalogsByType,
  setupCollapsibleSections,
  setupDragAndDrop,
  clearAllPreviews,
  initializeCatalogPreview,
  getCatalogPreview,
} from './ui';
import {
  showToast,
  copyToClipboard,
  setupPasswordToggle,
  validateAuthKey,
  validateCacheValidity,
  validateCatalogConfig,
  convertCacheTime,
  getLargestUnit,
  debounce,
  setCacheTTLLimits,
} from './utils';
import { validateConfig } from './api';
import { debugConsole } from './debug';

// Global configuration state
let currentConfig: Config = createDefaultConfig();

/**
 * Reload all active catalog previews with updated configuration
 */
function reloadAllPreviews(): void {
  debugConsole.log('[Main] 🔄 Reloading all active previews');

  // Iterate through all catalogs and reload their previews if they're enabled
  currentConfig.catalogs.forEach(catalog => {
    if (catalog.enabled) {
      const preview = getCatalogPreview(catalog.id);
      if (preview) {
        debugConsole.log(`[Main] 🔄 Reloading preview for catalog: ${catalog.id}`);

        // Re-encode the current config and update the preview's configHash
        try {
          const newConfigHash = encodeConfig(currentConfig);
          debugConsole.log(`[Main] ✅ New config hash generated: ${newConfigHash.substring(0, 20)}...`);
          // Update preview with new config hash
          (preview as any).configHash = newConfigHash;
          // Trigger reload
          preview.loadPreview();
        } catch (err) {
          debugConsole.error(`[Main] ❌ Failed to reload preview for ${catalog.id}: ${err}`);
        }
      }
    }
  });
}

// Create debounced version with 1 second delay
const debouncedReloadPreviews = debounce(reloadAllPreviews, 1000);

/**
 * Load configuration from URL if present, otherwise return defaults
 * URL pattern: /{configHash}/configure
 * Returns tuple: [config, wasLoadedFromURL]
 */
function loadConfigFromURL(): [Config, boolean] {
  // Parse URL path: "/{configHash}/configure"
  const pathParts = window.location.pathname.split('/').filter(p => p);

  // Check pattern: at least 2 parts, second part is "configure"
  if (pathParts.length >= 2 && pathParts[1] === 'configure') {
    const configHash = pathParts[0];  // First part is the hash
    debugConsole.log(`Found configHash in URL: ${configHash.substring(0, 10)}...`);

    try {
      // Use existing decodeConfig() from config.ts
      const decodedConfig = decodeConfig(configHash);
      debugConsole.log('Configuration decoded from URL successfully');
      return [decodedConfig, true];  // Loaded from URL

    } catch (error) {
      // Decode failed - corrupted hash, invalid protobuf, etc.
      debugConsole.log(`Failed to decode config from URL: ${error}`);
      showToast('⚠️ Invalid configuration URL - loading defaults', 'error');
    }
  }

  // No config in URL or decode failed - return defaults
  debugConsole.log('No configHash in URL, using defaults');
  return [createDefaultConfig(), false];  // Fresh defaults
}

/**
 * Transition to authenticated state - show post-auth UI
 */
function transitionToAuthenticatedState(authKey: string, email: string): void {
  // Hide pre-auth section, show post-auth section
  const preAuth = document.getElementById('pre-auth');
  const postAuth = document.getElementById('post-auth');
  const userEmail = document.getElementById('userEmail');
  const authKeyDisplay = document.getElementById('authKeyDisplay') as HTMLInputElement;

  if (preAuth) preAuth.style.display = 'none';
  if (postAuth) postAuth.style.display = 'block';
  if (userEmail) userEmail.textContent = email;
  if (authKeyDisplay) authKeyDisplay.value = authKey;

  // Setup password toggle for the display field
  setupPasswordToggle('authKeyDisplay', 'toggleAuthKeyDisplay');

  // Show sections that require authentication
  showAuthRequiredSections();
}

/**
 * Show sections that require authentication (Catalogs, Install Addon)
 */
function showAuthRequiredSections(): void {
  const authRequiredSections = document.querySelectorAll('.requires-auth');
  authRequiredSections.forEach(section => {
    (section as HTMLElement).style.display = 'block';
  });
}

/**
 * Hide sections that require authentication
 */
function hideAuthRequiredSections(): void {
  const authRequiredSections = document.querySelectorAll('.requires-auth');
  authRequiredSections.forEach(section => {
    (section as HTMLElement).style.display = 'none';
  });
}

/**
 * Show pre-auth state with auth key pre-filled
 * Used when auth key is expired or validation fails
 */
function showPreAuthStateWithKey(authKey: string): void {
  // Keep pre-auth UI visible, but pre-fill the auth key
  const authKeyInput = document.getElementById('authKey') as HTMLInputElement;
  if (authKeyInput) {
    authKeyInput.value = authKey;
  }
  // User can see their key and re-login if needed
  // Hide auth-required sections
  hideAuthRequiredSections();
}

/**
 * Auto-authenticate using decoded config's auth key
 * Transitions to authenticated state on success, shows warning on failure
 */
async function authenticateFromDecodedConfig(authKey: string): Promise<void> {
  debugConsole.log('Auto-authenticating from decoded config');

  try {
    // Call existing /api/auth/validate endpoint
    const response = await fetch('/api/auth/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authKey }),
    });

    const data = await response.json();

    if (data.success) {
      // Auth succeeded - switch to post-auth UI state
      const email = data.email || 'Unknown User';
      transitionToAuthenticatedState(authKey, email);

      // Show success toast with email if available, otherwise generic message
      const toastMsg = data.email
        ? `✅ Restored session for ${data.email}`
        : '✅ Session restored successfully';
      showToast(toastMsg, 'success');
      debugConsole.log(`Auto-authentication successful: ${email}`);

    } else {
      // Auth key is invalid/expired
      showPreAuthStateWithKey(authKey);
      showToast('⚠️ Auth key expired - please re-authenticate', 'warning');
      debugConsole.log('Auth key validation failed - expired or invalid');
    }

  } catch (error) {
    // Network error or API failure
    debugConsole.log(`Auth validation failed: ${error}`);
    showPreAuthStateWithKey(authKey);
    showToast('⚠️ Could not validate auth key - please verify', 'warning');
  }
}

/**
 * Initialize the application
 */
function initApp(): void {
  debugConsole.log('🚀 Librario initializing');

  // Read cache TTL limits from HTML data attributes (injected by backend template)
  const bodyEl = document.body;
  const minTTLAttr = bodyEl.getAttribute('data-min-cache-ttl');
  const maxTTLAttr = bodyEl.getAttribute('data-max-cache-ttl');

  if (minTTLAttr && maxTTLAttr) {
    const minTTL = parseInt(minTTLAttr);
    const maxTTL = parseInt(maxTTLAttr);
    setCacheTTLLimits(minTTL, maxTTL);
    debugConsole.log(`Cache TTL limits loaded from HTML: min=${minTTL}s, max=${maxTTL}s`);
  } else {
    debugConsole.log('Cache TTL limits not found in HTML, using hardcoded defaults');
  }

  // Load config from URL if present, otherwise use defaults
  const [loadedConfig, wasLoadedFromURL] = loadConfigFromURL();
  currentConfig = loadedConfig;

  // If not loaded from URL, set cache validity to match template's pre-filled minimum value
  if (!wasLoadedFromURL && minTTLAttr) {
    const minTTL = parseInt(minTTLAttr);
    currentConfig.cacheValiditySeconds = minTTL;
    debugConsole.log(`[Main] Set default cache validity to minimum: ${minTTL}s`);
  }

  // Setup UI components
  setupAuthUI();

  // Register callback for auth success (breaks circular dependency)
  setOnAuthSuccessCallback(updateAuthKeyAndInitializePreviews);

  setupPasswordToggle('authKey', 'toggleAuthKey');
  setupPasswordToggle('password', 'togglePassword');
  setupPasswordToggle('rpdbApiKey', 'toggleRpdbKey');
  setupPasswordToggle('tmdbApiKey', 'toggleTmdbKey');
  setupCollapsibleSections();

  // Setup drag-and-drop with order change callback
  setupDragAndDrop(handleCatalogOrderChange);

  // Restore form fields from loaded config (including global sort order)
  restoreFormFromConfig(currentConfig, wasLoadedFromURL);

  // Setup event listeners AFTER restoring form to ensure state synchronization
  setupEventListeners();

  // CRITICAL: Only call updateCatalogsByType for fresh defaults, NOT for URL-loaded configs
  // URL-loaded configs already have the correct catalog structure
  if (!wasLoadedFromURL) {
    currentConfig = updateCatalogsByType(currentConfig.groupByType, currentConfig);
    debugConsole.log(`Initial setup (defaults) - groupByType: ${currentConfig.groupByType}, catalogs: ${currentConfig.catalogs.length}`);
  } else {
    debugConsole.log(`Loaded from URL - preserving ${currentConfig.catalogs.length} catalogs from config`);
  }

  // Render initial catalog list
  renderCatalogList(currentConfig);

  // Initialize previews for all catalogs after rendering
  initializeAllPreviews();

  // Auto-authenticate if we have an auth key from URL
  if (currentConfig.authKey && currentConfig.authKey !== '') {
    authenticateFromDecodedConfig(currentConfig.authKey);
  }

  // Keyboard shortcut for generate (Ctrl/Cmd + Enter)
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      generateManifestUrl();
    }
  });

  // Prevent click-through on tooltip/info buttons
  document.querySelectorAll('.tooltip').forEach(tooltip => {
    tooltip.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
    });
  });

  debugConsole.log('✅ Librario initialized successfully');
}

/**
 * Initialize preview for all catalogs
 */
function initializeAllPreviews(): void {
  debugConsole.log('[Main] Initializing previews for all catalogs');

  if (!currentConfig.authKey) {
    debugConsole.log('[Main] No auth key, skipping preview initialization');
    return;
  }

  // Generate config hash for preview API calls
  const configHash = encodeConfig(currentConfig);

  currentConfig.catalogs.forEach(catalog => {
    debugConsole.log(`[Main] Initializing preview for ${catalog.id} (enabled: ${catalog.enabled})`);
    initializeCatalogPreview(
      catalog.id,
      catalog.type,
      configHash,
      catalog.enabled
    );
  });
}

/**
 * Update auth key and reinitialize previews
 * Called after successful authentication via callback from auth.ts
 */
function updateAuthKeyAndInitializePreviews(authKey: string): void {
  debugConsole.log('[Main] Updating auth key and reinitializing previews');
  currentConfig.authKey = authKey;
  showAuthRequiredSections();
  initializeAllPreviews();
}

/**
 * Update visibility of metadata-related fields based on toggle and provider selection
 */
function updateMetadataFieldsVisibility(): void {
  const metadataToggle = document.getElementById('enableMetadataFetching') as HTMLInputElement;
  const providerGroup = document.getElementById('metadataProviderGroup') as HTMLElement;
  const tmdbKeyGroup = document.getElementById('tmdbApiKeyGroup') as HTMLElement;
  const providerSelect = document.getElementById('metadataProvider') as HTMLSelectElement;

  if (!metadataToggle || !providerGroup || !tmdbKeyGroup || !providerSelect) {
    return;
  }

  const metadataEnabled = metadataToggle.checked;
  const providerValue = providerSelect.value;
  const needsTMDBKey = providerValue === '1' || providerValue === '2'; // TMDB or Auto

  // Show provider dropdown only if metadata is enabled
  providerGroup.style.display = metadataEnabled ? 'block' : 'none';

  // Show TMDB key only if metadata is enabled AND (TMDB or Auto is selected)
  tmdbKeyGroup.style.display = (metadataEnabled && needsTMDBKey) ? 'block' : 'none';
}

/**
 * Apply disabled state to catalog fields
 */
function applyCatalogDisabledState(catalogIndex: number, isEnabled: boolean): void {
  const catalogItems = document.querySelectorAll('.catalog-item');
  if (catalogIndex < 0 || catalogIndex >= catalogItems.length) return;

  const catalogItem = catalogItems[catalogIndex];

  // Get all editable fields within this catalog (sort is now global, not per-catalog)
  const nameInput = catalogItem.querySelector('.catalog-name-input') as HTMLInputElement;
  const typeInput = catalogItem.querySelector('.catalog-type-input') as HTMLInputElement;
  const typeLabel = catalogItem.querySelector('.catalog-type-label') as HTMLElement;
  const removedCheckbox = catalogItem.querySelector('.catalog-show-removed') as HTMLInputElement;

  const fields = [nameInput, typeInput, removedCheckbox].filter(Boolean);

  if (isEnabled) {
    // Enable all fields
    fields.forEach(field => field.removeAttribute('disabled'));
    catalogItem.classList.remove('catalog-disabled');
  } else {
    // Disable all fields
    fields.forEach(field => field.setAttribute('disabled', 'true'));
    catalogItem.classList.add('catalog-disabled');
  }
}

/**
 * Handle catalog order change from drag-and-drop
 */
function handleCatalogOrderChange(newOrder: string[]): void {
  debugConsole.log(`[Main] Catalog order changed: ${newOrder.join(', ')}`);

  // Reorder currentConfig.catalogs to match newOrder
  const reordered = newOrder
    .map(id => currentConfig.catalogs.find(c => c.id === id))
    .filter(Boolean) as CatalogConfig[];

  currentConfig.catalogs = reordered;

  // Update position field (for protobuf encoding)
  currentConfig.catalogs.forEach((cat, idx) => {
    cat.position = idx;
  });

  // CRITICAL: Update data-catalog-index attributes in DOM to match new order
  // Without this, event handlers will update the wrong catalog after reordering
  const catalogItems = document.querySelectorAll('.catalog-item');
  catalogItems.forEach((item, newIndex) => {
    // Update all elements with data-catalog-index attribute (sort is now global, not per-catalog)
    const nameInput = item.querySelector('.catalog-name-input');
    const typeInput = item.querySelector('.catalog-type-input');
    const removedToggle = item.querySelector('.catalog-show-removed');
    const enableToggle = item.querySelector('.catalog-toggle-enabled');

    [nameInput, typeInput, removedToggle, enableToggle].forEach(el => {
      if (el) {
        el.setAttribute('data-catalog-index', newIndex.toString());
      }
    });
  });

  debugConsole.log(`[Main] Config updated with new catalog order`);
}

/**
 * Setup all event listeners
 */
function setupEventListeners(): void {
  
  // Group by type toggle
  const groupByTypeCheckbox = document.getElementById('groupByType') as HTMLInputElement;
  groupByTypeCheckbox?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    currentConfig.groupByType = target.checked;

    // CRITICAL: Update catalogs while preserving Continue Watching
    currentConfig = updateCatalogsByType(target.checked, currentConfig);

    // Double-check Continue Watching is still there
    const hasCW = currentConfig.catalogs.some(c => c.isContinueWatching);
    debugConsole.log(`After groupByType toggle: Continue Watching present = ${hasCW}`);

    // Clear old previews
    clearAllPreviews();

    // Re-render catalog list
    renderCatalogList(currentConfig);

    // Reinitialize previews
    initializeAllPreviews();
  });

  // Catalog list event delegation
  const catalogList = document.getElementById('catalogList');
  catalogList?.addEventListener('input', (e) => {
    const target = e.target as HTMLElement;

    if (target.classList.contains('catalog-name-input')) {
      const index = parseInt(target.getAttribute('data-catalog-index') || '0');
      if (index >= 0 && index < currentConfig.catalogs.length) {
        currentConfig.catalogs[index].name = (target as HTMLInputElement).value;
      }
    } else if (target.classList.contains('catalog-type-input')) {
      const index = parseInt(target.getAttribute('data-catalog-index') || '0');
      if (index >= 0 && index < currentConfig.catalogs.length) {
        // Convert to lowercase for manifest
        const typeValue = (target as HTMLInputElement).value.toLowerCase();
        currentConfig.catalogs[index].type = typeValue as any;
      }
    }
  });

  catalogList?.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    const index = target.getAttribute('data-catalog-index');

    if (!index) return;
    const catalogIndex = parseInt(index);

    // Bounds checking
    if (catalogIndex < 0 || catalogIndex >= currentConfig.catalogs.length) {
      debugConsole.error(`Invalid catalog index: ${catalogIndex}`);
      return;
    }

    if (target.tagName === 'SELECT') {
      const field = target.getAttribute('data-field');
      const value = (target as HTMLSelectElement).value;

      if (field === 'type') {
        currentConfig.catalogs[catalogIndex].type = value as any;
      } else if (field === 'sort') {
        currentConfig.catalogs[catalogIndex].sortOrder = value as any;
        debugConsole.log(`[Main] 🔀 Sort changed for catalog ${catalogIndex} to: ${value}`);
        debouncedReloadPreviews();
      }
    } else if (target.type === 'checkbox' && target.classList.contains('catalog-show-removed')) {
      // Show Removed toggle (now a toggle switch instead of checkbox)
      const oldValue = currentConfig.catalogs[catalogIndex].showRemovedItems;
      currentConfig.catalogs[catalogIndex].showRemovedItems = (target as HTMLInputElement).checked;
      debugConsole.log(`[Main] 👁️  Show Removed changed for catalog ${catalogIndex}: ${oldValue} → ${(target as HTMLInputElement).checked}`);
      // Trigger debounced preview reload for this catalog
      debugConsole.log('[Main] ⏱️  Triggering debounced reload (1 second delay)...');
      debouncedReloadPreviews();
    } else if (target.type === 'checkbox' && target.classList.contains('catalog-toggle-enabled')) {
      // Catalog enable/disable toggle
      const isEnabled = (target as HTMLInputElement).checked;
      currentConfig.catalogs[catalogIndex].enabled = isEnabled;

      // Apply disabled state to fields
      applyCatalogDisabledState(catalogIndex, isEnabled);

      // Handle preview loading/clearing
      const catalog = currentConfig.catalogs[catalogIndex];
      const preview = getCatalogPreview(catalog.id);
      if (preview) {
        if (isEnabled) {
          debugConsole.log(`[Main] Catalog ${catalog.id} enabled, loading preview`);
          preview.loadPreview();
        } else {
          debugConsole.log(`[Main] Catalog ${catalog.id} disabled, clearing preview`);
          preview.clear();
        }
      }
    }
  });

  // Cache settings
  const cacheValueInput = document.getElementById('cacheValue') as HTMLInputElement;
  const cacheUnitSelect = document.getElementById('cacheUnit') as HTMLSelectElement;

  const updateCacheValidity = () => {
    const value = parseInt(cacheValueInput.value);
    const unit = cacheUnitSelect.value as 'seconds' | 'minutes' | 'hours';
    currentConfig.cacheValiditySeconds = convertCacheTime(value, unit);
  };

  cacheValueInput?.addEventListener('change', updateCacheValidity);
  cacheUnitSelect?.addEventListener('change', updateCacheValidity);

  // RPDB API key
  const rpdbKeyInput = document.getElementById('rpdbApiKey') as HTMLInputElement;
  rpdbKeyInput?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    currentConfig.rpdbApiKey = target.value.trim() || undefined;
  });

  // Addon name
  const addonNameInput = document.getElementById('addonName') as HTMLInputElement;
  addonNameInput?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    currentConfig.addonName = target.value.trim() || undefined;
  });

  // Addon description
  const addonDescInput = document.getElementById('addonDescription') as HTMLTextAreaElement;
  addonDescInput?.addEventListener('change', (e) => {
    const target = e.target as HTMLTextAreaElement;
    currentConfig.addonDescription = target.value.trim() || undefined;
  });

  // Items per Page handling
  const itemsPerPageInput = document.getElementById('itemsPerPage') as HTMLInputElement;

  const updateItemsPerPage = () => {
    const value = parseInt(itemsPerPageInput.value);
    if (value >= 10 && value <= 500) {
      currentConfig.catalogs.forEach(catalog => {
        catalog.itemsPerPage = value;
      });
    }
  };

  itemsPerPageInput?.addEventListener('change', updateItemsPerPage);

  // Metadata fetching toggle
  const metadataToggle = document.getElementById('enableMetadataFetching') as HTMLInputElement;
  metadataToggle?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    currentConfig.enableMetadataFetching = target.checked;
    updateMetadataFieldsVisibility();
  });

  // Metadata provider dropdown
  const providerSelect = document.getElementById('metadataProvider') as HTMLSelectElement;
  providerSelect?.addEventListener('change', (e) => {
    const target = e.target as HTMLSelectElement;
    currentConfig.metadataProvider = parseInt(target.value);
    updateMetadataFieldsVisibility();
  });

  // TMDB API key
  const tmdbKeyInput = document.getElementById('tmdbApiKey') as HTMLInputElement;
  tmdbKeyInput?.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    currentConfig.tmdbApiKey = target.value.trim() || undefined;
  });

  // Generate manifest button
  const generateBtn = document.getElementById('generateManifest');
  generateBtn?.addEventListener('click', generateManifestUrl);

  // Copy URL button
  const copyBtn = document.getElementById('copyUrl');
  copyBtn?.addEventListener('click', async () => {
    const urlInput = document.getElementById('manifestUrl') as HTMLInputElement;
    const success = await copyToClipboard(urlInput.value);
    if (success) {
      showToast('📋 URL copied to clipboard!', 'success');
      urlInput.style.background = 'rgba(16, 185, 129, 0.2)';
      setTimeout(() => {
        urlInput.style.background = '';
      }, 500);
    } else {
      showToast('Failed to copy URL', 'error');
    }
  });

  // Shareable config button
  const shareBtn = document.getElementById('shareConfig');
  shareBtn?.addEventListener('click', generateShareableConfig);
}

/**
 * Generate manifest URL from current configuration
 */
async function generateManifestUrl(): Promise<void> {
  debugConsole.log('Generating manifest URL');

  // Get auth key from either input (pre-auth or post-auth state)
  const authKeyInput = document.getElementById('authKey') as HTMLInputElement;
  const authKeyDisplayInput = document.getElementById('authKeyDisplay') as HTMLInputElement;

  debugConsole.log(`authKeyInput exists: ${!!authKeyInput}, value length: ${authKeyInput?.value?.length || 0}`);
  debugConsole.log(`authKeyDisplayInput exists: ${!!authKeyDisplayInput}, value length: ${authKeyDisplayInput?.value?.length || 0}`);

  // Try post-auth input first, then pre-auth input
  const authKey = (authKeyDisplayInput?.value || authKeyInput?.value || '').trim();
  debugConsole.log(`Final authKey length: ${authKey.length}, first 10 chars: ${authKey.substring(0, 10)}...`);
  currentConfig.authKey = authKey;

  // Validate addon name length
  if (currentConfig.addonName && currentConfig.addonName.length > 50) {
    showToast('❌ Addon name must be 50 characters or less', 'error');
    return;
  }

  // Validate addon description length
  if (currentConfig.addonDescription && currentConfig.addonDescription.length > 200) {
    showToast('❌ Addon description must be 200 characters or less', 'error');
    return;
  }

  // Validate
  const authValidation = validateAuthKey(currentConfig.authKey);
  if (!authValidation.valid) {
    showToast(authValidation.message || 'Invalid auth key', 'error');
    authKeyInput.focus();
    return;
  }

  const cacheValidation = validateCacheValidity(currentConfig.cacheValiditySeconds);
  if (!cacheValidation.valid) {
    showToast(cacheValidation.message || 'Invalid cache validity', 'error');
    return;
  }

  const catalogValidation = validateCatalogConfig(currentConfig.catalogs);
  if (!catalogValidation.valid) {
    showToast(catalogValidation.message || 'Invalid catalog configuration', 'error');
    return;
  }

  const configValidation = await validateConfig(currentConfig);
  if (!configValidation) {
    showToast('Configuration validation failed. Check debug console for details.', 'error');
    return;
  }

  try {
    // Encode configuration
    const encodedConfig = encodeConfig(currentConfig);

    // Generate URLs
    const protocol = window.location.protocol;
    const host = window.location.host;
    const manifestUrl = `${protocol}//${host}/${encodedConfig}/manifest.json`;
    const stremioUrl = `stremio://${host}/${encodedConfig}/manifest.json`;

    // Display result
    const manifestOutput = document.getElementById('manifestOutput');
    const manifestUrlInput = document.getElementById('manifestUrl') as HTMLInputElement;
    const installLink = document.getElementById('installInStremio') as HTMLAnchorElement;

    if (manifestUrlInput) {
      manifestUrlInput.value = manifestUrl;
    }

    if (installLink) {
      installLink.href = stremioUrl;
    }

    if (manifestOutput) {
      manifestOutput.style.display = 'block';
      manifestOutput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    showToast('✨ Addon URL generated successfully!', 'success');
    debugConsole.log(`Manifest URL generated: ${manifestUrl.length} chars`);
  } catch (error) {
    showToast(`Failed to generate manifest: ${error}`, 'error');
    debugConsole.log(`Error generating manifest: ${error}`);
  }
}

/**
 * Generate shareable configuration (no credentials)
 */
function generateShareableConfig(): void {
  debugConsole.log('Generating shareable configuration');

  try {
    const shareable = createShareableConfig(currentConfig);
    const encoded = encodeConfig(shareable);

    const protocol = window.location.protocol;
    const host = window.location.host;
    const shareableUrl = `${protocol}//${host}/${encoded}/configure`;

    const success = copyToClipboard(shareableUrl);
    if (success) {
      showToast('🔗 Shareable config URL copied!', 'success');
      debugConsole.log('Shareable config generated and copied');
    } else {
      showToast('Failed to copy shareable URL', 'error');
    }
  } catch (error) {
    showToast(`Failed to generate shareable config: ${error}`, 'error');
    debugConsole.log(`Error generating shareable config: ${error}`);
  }
}

/**
 * Restore form from configuration
 * @param config Configuration to restore from
 * @param wasLoadedFromURL If true, cache settings are restored from config; if false, template defaults are kept
 */
function restoreFormFromConfig(config: Config, wasLoadedFromURL: boolean): void {
  debugConsole.log('Restoring form from saved configuration');

  // Group by type
  const groupByTypeCheckbox = document.getElementById('groupByType') as HTMLInputElement;
  if (groupByTypeCheckbox) {
    groupByTypeCheckbox.checked = config.groupByType;
  }

  // Global sort order - use the first catalog's sort order as the global value
  const sortOrderSelect = document.getElementById('sortOrder') as HTMLSelectElement;
  if (sortOrderSelect && config.catalogs.length > 0) {
    // Find the first non-Continue Watching catalog for the global sort order
    const firstRegularCatalog = config.catalogs.find(c => !c.isContinueWatching);
    const sortCatalog = firstRegularCatalog || config.catalogs[0];

    // Map string sort order to numeric value for the dropdown
    const numericSortValue = mapStringSortToNumeric(sortCatalog.sortOrder);
    sortOrderSelect.value = numericSortValue;
    debugConsole.log(`[Main] Restored global sort order: ${numericSortValue} (${sortCatalog.sortOrder})`);
  }

  // Cache settings - only override if loaded from URL, otherwise keep template defaults
  if (wasLoadedFromURL) {
    const cacheTime = getLargestUnit(config.cacheValiditySeconds);
    const cacheValueInput = document.getElementById('cacheValue') as HTMLInputElement;
    const cacheUnitSelect = document.getElementById('cacheUnit') as HTMLSelectElement;
    if (cacheValueInput) {
      cacheValueInput.value = String(cacheTime.value);
    }
    if (cacheUnitSelect) {
      cacheUnitSelect.value = cacheTime.unit;
    }
    debugConsole.log(`[Main] Restored cache settings from URL: ${cacheTime.value} ${cacheTime.unit}`);
  } else {
    debugConsole.log('[Main] Keeping template-rendered cache settings (not loaded from URL)');
  }

  // RPDB API key
  if (config.rpdbApiKey) {
    const rpdbKeyInput = document.getElementById('rpdbApiKey') as HTMLInputElement;
    if (rpdbKeyInput) {
      rpdbKeyInput.value = config.rpdbApiKey;
    }
  }

  // Addon name
  if (config.addonName) {
    const addonNameInput = document.getElementById('addonName') as HTMLInputElement;
    if (addonNameInput) {
      addonNameInput.value = config.addonName;
    }
  }

  // Addon description
  if (config.addonDescription) {
    const addonDescInput = document.getElementById('addonDescription') as HTMLTextAreaElement;
    if (addonDescInput) {
      addonDescInput.value = config.addonDescription;
    }
  }

  // Metadata fetching toggle
  const metadataToggle = document.getElementById('enableMetadataFetching') as HTMLInputElement;
  if (metadataToggle) {
    metadataToggle.checked = config.enableMetadataFetching !== false; // Default: true
  }

  // Metadata provider
  const providerSelect = document.getElementById('metadataProvider') as HTMLSelectElement;
  if (providerSelect) {
    providerSelect.value = String(config.metadataProvider || 0);
  }

  // TMDB API key
  if (config.tmdbApiKey) {
    const tmdbKeyInput = document.getElementById('tmdbApiKey') as HTMLInputElement;
    if (tmdbKeyInput) {
      tmdbKeyInput.value = config.tmdbApiKey;
    }
  }

  // Update metadata fields visibility based on restored values
  updateMetadataFieldsVisibility();
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
